<template>
  <HrPageLayout
    title="Clock-In Locations"
    subtitle="Define company sites where employees can clock in on-site (geofence)"
    icon="📍"
    :breadcrumb-items="[
      { label: 'Attendance Management' },
      { label: 'Clock-In Locations' }
    ]"
  >
    <template #header-actions>
      <div class="hr-flex hr-flex-center hr-gap-2">
        <select class="hr-select" style="width: 140px;" v-model="statusFilter" @change="fetchLocations">
          <option value="">All status</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
        <button type="button" class="hr-btn hr-btn-success" @click="openAddModal">
          <i class="bx bx-plus-circle"></i> Add location
        </button>
      </div>
    </template>

    <div class="wl-content">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-map-pin"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total locations</p>
          <h2 class="hr-metric-value">{{ metrics.total || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Active</p>
          <h2 class="hr-metric-value">{{ metrics.active || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-x-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Inactive</p>
          <h2 class="hr-metric-value">{{ metrics.inactive || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-radar"></i></div>
          <p class="hr-metric-label">How it works</p>
          <p class="wl-hint">Employees within the radius are marked on-site at clock-in.</p>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-buildings"></i> Company locations</h5>
          <div class="hr-search-wrap" style="min-width: 260px;">
            <input
              v-model="searchValue"
              type="text"
              class="hr-input"
              placeholder="Search name, code, address…"
              @keyup.enter="fetchLocations"
            >
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading locations…</p>
          </div>
          <div v-else-if="locations.length === 0" class="hr-empty-state">
            <i class="bx bx-map"></i>
            <h5>No locations yet</h5>
            <p>Add offices, branches, or sites where staff may clock in on-site.</p>
            <button type="button" class="hr-btn hr-btn-primary" @click="openAddModal">
              <i class="bx bx-plus hr-me-2"></i> Add first location
            </button>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Location</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Coordinates</th>
                  <th style="text-align: center;">Radius</th>
                  <th style="text-align: center;">Status</th>
                  <th style="text-align: center;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in locations" :key="item.id">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <div class="wl-name">{{ item.location_name }}</div>
                    <small v-if="item.address" class="wl-address">{{ item.address }}</small>
                  </td>
                  <td><code class="wl-code">{{ item.location_code }}</code></td>
                  <td>
                    <span class="hr-badge hr-badge-info">{{ item.location_type || 'Office' }}</span>
                  </td>
                  <td class="wl-coords">
                    {{ formatCoord(item.latitude) }}, {{ formatCoord(item.longitude) }}
                    <a
                      :href="mapsUrl(item.latitude, item.longitude)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="wl-map-link"
                      title="Open in Google Maps"
                    >
                      <i class="bx bx-link-external"></i>
                    </a>
                  </td>
                  <td style="text-align: center;">
                    <span class="hr-badge hr-badge-primary">{{ item.radius_meters }} m</span>
                  </td>
                  <td style="text-align: center;">
                    <label class="hr-toggle-wrap">
                      <input
                        type="checkbox"
                        :checked="item.is_active == 1 || item.is_active === '1'"
                        @change="toggleStatus(item)"
                        :disabled="item.toggling"
                      >
                      <span class="hr-toggle-slider"></span>
                    </label>
                  </td>
                  <td style="text-align: center;">
                    <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: center;">
                      <button type="button" class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" @click="openEditModal(item)" title="Edit">
                        <i class="bx bx-edit"></i>
                      </button>
                      <button type="button" class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" @click="confirmDelete(item)" title="Delete">
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ModalWrapper
        ref="formModal"
        :title="isEditing ? 'Edit location' : 'Add clock-in location'"
        size="medium"
      >
        <template #body>
          <form id="wl-form" @submit.prevent="saveLocation" class="wl-form">
            <div class="hr-mb-3">
              <label class="hr-form-label">Location name <span class="required">*</span></label>
              <input v-model="formData.location_name" type="text" class="hr-input" placeholder="e.g. Head Office, Westlands Branch" required>
            </div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Short code <span class="required">*</span></label>
                <input
                  v-model="formData.location_code"
                  type="text"
                  class="hr-input"
                  placeholder="HQ"
                  maxlength="50"
                  required
                  @input="formData.location_code = formData.location_code.toUpperCase()"
                >
                <small class="hr-form-hint">Used in timesheets (unique)</small>
              </div>
              <div>
                <label class="hr-form-label">Type</label>
                <select v-model="formData.location_type" class="hr-select">
                  <option value="Office">Office</option>
                  <option value="Branch">Branch</option>
                  <option value="Site">Site</option>
                  <option value="Client">Client</option>
                  <option value="Remote">Remote</option>
                </select>
              </div>
            </div>
            <div class="hr-mb-3">
              <label class="hr-form-label">Address</label>
              <textarea v-model="formData.address" class="hr-textarea" rows="2" placeholder="Street, city, country"></textarea>
            </div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Latitude <span class="required">*</span></label>
                <input v-model="formData.latitude" type="text" class="hr-input" placeholder="-1.292066" required>
              </div>
              <div>
                <label class="hr-form-label">Longitude <span class="required">*</span></label>
                <input v-model="formData.longitude" type="text" class="hr-input" placeholder="36.821946" required>
              </div>
            </div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Geofence radius (meters) <span class="required">*</span></label>
                <input v-model.number="formData.radius_meters" type="number" class="hr-input" min="10" max="5000" step="10" required>
                <small class="hr-form-hint">10–5000 m. Default 100 m.</small>
              </div>
              <div class="wl-gps-col">
                <label class="hr-form-label">&nbsp;</label>
                <button type="button" class="hr-btn hr-btn-secondary wl-gps-btn" @click="useMyLocation" :disabled="gpsLoading">
                  <i class="bx bx-current-location"></i>
                  {{ gpsLoading ? 'Getting GPS…' : 'Use my current position' }}
                </button>
              </div>
            </div>
            <div>
              <label class="hr-checkbox-wrap">
                <input type="checkbox" v-model="formData.isActive">
                <span>Active (employees can clock in within this geofence)</span>
              </label>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.formModal.close()">Cancel</button>
          <button type="submit" form="wl-form" class="hr-btn hr-btn-success" :disabled="saving">
            <span v-if="saving" class="hr-spinner-inline"></span>
            <i v-else class="bx bx-check hr-me-2"></i>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </template>
      </ModalWrapper>

      <ModalWrapper ref="deleteModal" title="Remove location" size="small">
        <template #body>
          <div class="text-center">
            <i class="bx bx-error-circle delete-icon"></i>
            <h5>Delete {{ deleteItem?.location_name }}?</h5>
            <p class="hr-text-muted">If this location has attendance history it will be deactivated instead of deleted.</p>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.deleteModal.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-danger" :disabled="deleting" @click="deleteLocation">
            <span v-if="deleting" class="hr-spinner-inline"></span>
            <i v-else class="bx bx-trash hr-me-2"></i>
            Confirm
          </button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "WorkLocationsManagement",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      gpsLoading: false,
      searchValue: "",
      statusFilter: "",
      locations: [],
      metrics: { total: 0, active: 0, inactive: 0 },
      isEditing: false,
      formData: this.emptyForm(),
      deleteItem: null
    };
  },
  mounted() {
    this.fetchLocations();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        location_name: "",
        location_code: "",
        location_type: "Office",
        address: "",
        latitude: "",
        longitude: "",
        radius_meters: 100,
        isActive: true
      };
    },
    formatCoord(v) {
      const n = parseFloat(v);
      if (Number.isNaN(n)) return "—";
      return n.toFixed(6);
    },
    mapsUrl(lat, lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    },
    fetchLocations() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getWorkLocationsManagement");
      data.append("search", this.searchValue || "");
      data.append("status", this.statusFilter);

      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data?.success) {
            this.locations = res.data.data?.locations || [];
            this.metrics = res.data.data?.metrics || { total: 0, active: 0, inactive: 0 };
          } else {
            this.locations = [];
            this.showToast(res.data?.message || "Failed to load locations", "error");
          }
        })
        .catch(() => {
          this.loading = false;
          this.locations = [];
          this.showToast("Failed to load locations", "error");
        });
    },
    openAddModal() {
      this.isEditing = false;
      this.formData = this.emptyForm();
      this.$refs.formModal.open();
    },
    openEditModal(item) {
      this.isEditing = true;
      this.formData = {
        id: item.id,
        location_name: item.location_name || "",
        location_code: item.location_code || "",
        location_type: item.location_type || "Office",
        address: item.address || "",
        latitude: item.latitude != null ? String(item.latitude) : "",
        longitude: item.longitude != null ? String(item.longitude) : "",
        radius_meters: parseInt(item.radius_meters, 10) || 100,
        isActive: item.is_active == 1 || item.is_active === "1"
      };
      this.$refs.formModal.open();
    },
    useMyLocation() {
      if (!navigator.geolocation) {
        this.showToast("Geolocation is not supported in this browser", "error");
        return;
      }
      this.gpsLoading = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.gpsLoading = false;
          this.formData.latitude = String(pos.coords.latitude);
          this.formData.longitude = String(pos.coords.longitude);
          if (pos.coords.accuracy) {
            const suggested = Math.max(50, Math.min(500, Math.round(pos.coords.accuracy * 2)));
            this.formData.radius_meters = suggested;
          }
        },
        () => {
          this.gpsLoading = false;
          this.showToast("Could not get your location. Enter coordinates manually.", "error");
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    },
    saveLocation() {
      if (!this.formData.location_name?.trim() || !this.formData.location_code?.trim()) {
        this.showToast("Name and code are required", "error");
        return;
      }

      this.saving = true;
      const data = new FormData();
      data.append("function", this.isEditing ? "updateWorkLocation" : "addWorkLocation");
      if (this.isEditing) data.append("id", String(this.formData.id));
      data.append("location_name", this.formData.location_name.trim());
      data.append("location_code", this.formData.location_code.trim().toUpperCase());
      data.append("location_type", this.formData.location_type);
      data.append("address", (this.formData.address || "").trim());
      data.append("latitude", this.formData.latitude);
      data.append("longitude", this.formData.longitude);
      data.append("radius_meters", String(this.formData.radius_meters || 100));
      data.append("is_active", this.formData.isActive ? "1" : "0");

      execute(data, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data?.success) {
            this.$refs.formModal.close();
            this.showToast(res.data.message || "Saved", "success");
            this.fetchLocations();
          } else {
            this.showToast(res.data?.message || "Save failed", "error");
          }
        })
        .catch(() => {
          this.saving = false;
          this.showToast("Save failed", "error");
        });
    },
    toggleStatus(item) {
      const nextActive = !(item.is_active == 1 || item.is_active === "1");
      item.toggling = true;
      const data = new FormData();
      data.append("function", "toggleWorkLocationStatus");
      data.append("id", String(item.id));
      data.append("is_active", nextActive ? "1" : "0");

      execute(data, authUrl)
        .then((res) => {
          item.toggling = false;
          if (res.data?.success) {
            item.is_active = nextActive ? 1 : 0;
            this.fetchLocations();
          } else {
            this.showToast(res.data?.message || "Update failed", "error");
          }
        })
        .catch(() => {
          item.toggling = false;
          this.showToast("Update failed", "error");
        });
    },
    confirmDelete(item) {
      this.deleteItem = item;
      this.$refs.deleteModal.open();
    },
    deleteLocation() {
      if (!this.deleteItem) return;
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deleteWorkLocation");
      data.append("id", String(this.deleteItem.id));

      execute(data, authUrl)
        .then((res) => {
          this.deleting = false;
          this.$refs.deleteModal.close();
          if (res.data?.success) {
            this.showToast(res.data.message || "Removed", "success");
            this.fetchLocations();
          } else {
            this.showToast(res.data?.message || "Delete failed", "error");
          }
        })
        .catch(() => {
          this.deleting = false;
          this.showToast("Delete failed", "error");
        });
    },
    showToast(message, type) {
      if (this.$toast) {
        if (type === "success") this.$toast.success(message);
        else this.$toast.error(message);
      }
    }
  }
};
</script>

<style scoped>
.wl-content {
  max-width: 1320px;
  margin: 0 auto;
}

.wl-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  margin: 4px 0 0;
  line-height: 1.35;
}

.wl-name {
  font-weight: 600;
  color: #0f172a;
}

.wl-address {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-top: 2px;
}

.wl-code {
  font-size: 12px;
  background: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
}

.wl-coords {
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

.wl-map-link {
  margin-left: 6px;
  color: #2563eb;
  vertical-align: middle;
}

.wl-gps-col {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.wl-gps-btn {
  width: 100%;
}

.delete-icon {
  font-size: 48px;
  color: #ef4444;
  display: block;
  margin-bottom: 12px;
}

.required {
  color: #ef4444;
}
</style>
